import { useMutation, useQuery } from 'react-query';
import {
  isCreateResponseCode,
  isDeleteResponseCode,
  isSuccessResponseCode,
  isUpdateResponseCode,
  transformBankListsData,
} from './const';
import {
  bulkPostBanks,
  deleteBanks,
  getBankDetails,
  getBankLists,
  getBanksDropdown,
  updateBanks,
} from '../../../../api/banks';
import { useEffect, useState } from 'react';
import queryClient from '../../../../hooks/queryClient';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
  getAllBankDepository,
  getAllDMBankDepository,
} from '../../../../api/collections';
import { isUndefined, toUpper } from 'lodash';

export const useBankListsQuery = () => {
  const [params] = useState({});

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['bank_lists', params],
    queryFn: async () => {
      const res = await getBankLists(params);

      const { code, result } = res.data.response;

      isSuccessResponseCode(code, result);

      return transformBankListsData(result);
    },
  });

  useEffect(() => {
    if (isLoading) {
      return toast.loading('Processing your request please wait...');
    }

    return toast.dismiss();
  }, [isLoading]);

  return {
    data: data === undefined ? [] : data,
    isLoading,
    isFetching,
    refetch,
  };
};

export const useBankCreateMutation = () => {
  return useMutation({
    mutationFn: bulkPostBanks,
    onSuccess: async (res) => {
      const { code, result } = res.data.response;

      isCreateResponseCode(code, result);

      const { isConfirmed } = await Swal.fire({
        title: 'Banks',
        text: 'Successfully created record(s)?',
        icon: 'success',
        showConfirmButton: true,
      });

      queryClient.invalidateQueries(['bank_lists']);

      return { isSwalConfirmed: isConfirmed };
    },
    onError: (error) => {
      toast.dismiss();
      toast.error(error.message);
    },
  });
};

export const useBankDeleteMutation = () => {
  return useMutation(deleteBanks, {
    onSuccess: async (res) => {
      const { code, result } = res.data.response;

      isDeleteResponseCode(code, result);

      queryClient.invalidateQueries(['bank_lists']);
    },
    onError: (error) => {
      toast.dismiss();
      toast.error(error.message);
    },
  });
};

export const useBankUpdateMutation = () => {
  return useMutation({
    mutationFn: ({ data }) => {
      let id = data.banks_id;
      return updateBanks(data, id);
    },
    onSuccess: async (res) => {
      const { code, result } = res.data.response;

      isUpdateResponseCode(code, result);

      queryClient.invalidateQueries(['bank_lists']);
    },
    onError: (error) => {
      toast.dismiss();
      toast.error(error.message);
    },
  });
};

export const useBankShowQuery = (id) => {
  const { data, isLoading, isFetched } = useQuery({
    queryKey: ['bank_lists', id],
    queryFn: async () => {
      if (id === null) return;
      const res = await getBankDetails(id);

      const { code, result } = res.data.response;

      isSuccessResponseCode(code, result);

      return result;
    },
  });
  return {
    bankDetails: data === undefined ? [] : data,
    isLoading,
    isFetched,
  };
};

export const useBankDepositoryLists = () => {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['bank_dropdown_depository_lists'],
    queryFn: async () => {
      const res = await getAllBankDepository();

      const { code, result } = res.data.response;

      isSuccessResponseCode(code, result);

      return result.map(({ banks_id, bank_name, bank_shortname }) => ({
        value: banks_id,
        label: toUpper(bank_name),
        shortname: bank_shortname,
      }));
    },
  });

  return {
    bankDropdown: isUndefined(data) ? [] : data,
    isLoading,
    isFetchingBank: isFetching,
    refetchBank: refetch,
  };
};

export const useDMBankDepositoryLists = () => {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['bank_dropdown_depository_lists'],
    queryFn: async () => {
      const res = await getAllDMBankDepository();

      const { code, result } = res.data.response;

      isSuccessResponseCode(code, result);

      return result.map(({ banks_id, bank_name, bank_shortname }) => ({
        value: banks_id,
        label: toUpper(bank_name),
        shortname: bank_shortname,
      }));
    },
  });

  return {
    bankDropdown: isUndefined(data) ? [] : data,
    isLoading,
    isFetchingBank: isFetching,
    refetchBank: refetch,
  };
};

export const useBanksDropdownLists = () => {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['bank_dropdown_lists'],
    queryFn: async () => {
      const res = await getBanksDropdown();

      const { code, result } = res.data.response;

      isSuccessResponseCode(code, result);

      return result.map(({ banks_id, bank_name }) => ({
        value: banks_id,
        label: toUpper(bank_name),
      }));
    },
  });

  return {
    bankOptions: isUndefined(data) ? [] : data,
    isLoading,
    isFetchingBank: isFetching,
    refetchBank: refetch,
  };
};
